import React, { useEffect, useState, useRef } from 'react';
import {
  GoogleMap,
  InfoWindow,
  Marker,
  Polyline,
  useJsApiLoader,
} from '@react-google-maps/api';
import {
  INDIA_BOUNDS,
  TEST,
  DEFAULT_MAP_BOUNDS,
} from '../../utilities/constants';
import DefaultInfoWindowContent from './DefaultInfoWindowContent';
import { Alert, Button, ConfigProvider, Flex, Tooltip, Typography } from 'antd';
import EmptyCard from '../EmptyCard/EmptyCard';
import MapLegend from '../MapLegend/MapLegend';
import LegendRashDrivingSvg from '../../assets/icons/map-legend-rash-driving.svg';
import LegendDriverDistractionSvg from '../../assets/icons/map-legend-driver-distraction.svg';
import LegendDriverDrowsinessSvg from '../../assets/icons/map-legend-driver-drowsiness.svg';
import LegendCurrentLocationSvg from '../../assets/icons/map-legend-current-location.svg';
import LegendSOSSvg from '../../assets/icons/map-legend-sos.svg';
import LegendSOSInprogressSvg from '../../assets/icons/map-legend-sos-inprogress.svg';
import LegendSOSResolvedSvg from '../../assets/icons/map-legend-sos-resolved.svg';
import LegendTripStartSvg from '../../assets/icons/map-legend-trip-start.svg';
import LegendOverspeedSvg from '../../assets/icons/map-legend-overspeed.svg';
import LegendTripEndSvg from '../../assets/icons/map-legend-trip-end.svg';
import LegendActualPickupDropSvg from '../../assets/icons/map-legend-actual-pickup-drop.svg';
import LegendScheduledPickupDropSvg from '../../assets/icons/map-legend-scheduled-pickup-drop.svg';
import LegendOpenDoorSvg from '../../assets/icons/map-legend-door-open.svg';
import { ReactComponent as Recenter } from '../../assets/icons/map-recenter.svg';
import {
  createDimmedMarker,
  kebabToTitleCase,
} from '../../utilities/commonFunctions';
import Icon, { LoadingOutlined, CheckCircleOutlined } from '@ant-design/icons';
import MapFilter from '../MapFilter/MapFilter';
import RotatedMarker from './RotatedMarker';
import PropTypes from 'prop-types';

const defaultContainerStyle = {
  width: '100%',
  height: '100%',
  minHeight: '100px',
  minWidth: '100px',
};

const libraries = ['places'];

const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_JS_API_KEY;

const getGoogleMapsUnavailableMessage = loadError => {
  //eslint-disable-next-line no-console
  console.log('Google Maps load error:', loadError);
  const errorMessage = loadError?.message?.toLowerCase?.() || '';

  if (errorMessage.includes('RefererNotAllowedMapError')) {
    return 'Maps is unavailable. Please verify the Google Maps API key restrictions and authorized domains.';
  }

  return 'Maps is unavailable.';
};

const baseStyles = [
  {
    featureType: 'poi',
    elementType: 'labels',
    stylers: [
      {
        visibility: 'off',
      },
    ],
  },
  {
    featureType: 'poi.business',
    stylers: [
      {
        visibility: 'on',
      },
    ],
  },
  {
    featureType: 'poi.business',
    elementType: 'labels',
    stylers: [
      {
        visibility: 'off',
      },
    ],
  },
  {
    featureType: 'poi.government',
    stylers: [
      {
        visibility: 'on',
      },
    ],
  },
  {
    featureType: 'poi.medical',
    stylers: [
      {
        visibility: 'on',
      },
    ],
  },
  {
    featureType: 'poi.park',
    stylers: [
      {
        visibility: 'on',
      },
    ],
  },
];

const ZOOM_BLOCK_EVENTS = [
  'wheel',
  'gesturestart',
  'touchmove',
  'pointermove',
  'mousedown',
];

const stopZoom = e => {
  e.preventDefault();
  e.stopPropagation();
  e.stopImmediatePropagation();
};

// Small wrapper that blocks zoom on its DOM subtree
const ZoomBlocker = ({ children }) => {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    ZOOM_BLOCK_EVENTS.forEach(evt =>
      el.addEventListener(evt, stopZoom, { passive: false, capture: true })
    );

    return () => {
      ZOOM_BLOCK_EVENTS.forEach(evt =>
        el.removeEventListener(evt, stopZoom, { capture: true })
      );
    };
  }, []);

  return (
    <div
      ref={ref}
      style={{ touchAction: 'pan-x pan-y', overscrollBehavior: 'contain' }}
    >
      {children}
    </div>
  );
};

ZoomBlocker.propTypes = {
  children: PropTypes.node.isRequired,
};

const GoogleMaps = ({
  polylines,
  infoWindow,
  closeInfoWindow,
  center,
  showLegend = false,
  mapFilterProps = {},
  showFilter = false,
  isMapLegendVertical = false,
  markers = [],
  circles = [],
  customStyle = defaultContainerStyle,
  showCustomFullScreenControl = false,
  CustomFullScreenControl = <></>,
  isGPSLocationUpdated = false,
  gpsLoadingMessage = null,
  InfoWindowContent = DefaultInfoWindowContent,
  isInfoWindowClosable = true,
  showMarkerHoverEffect = false,
  showMarkerHoverEffectOnHovered = false,
  hoveredMarker = null,
  enableRecenter = false,
  preserveViewportOnUserInteraction = false,
  onUserInteraction,
}) => {
  const { Text } = Typography;
  const isMissingGoogleMapsApiKey = !googleMapsApiKey;

  const [mapCircles, setMapCircles] = useState([]);
  const googleMapsAuthError = false;
  const [showInfoWindow, setShowInfoWindow] = useState(false);
  const [mapLegendList, setMapLegendList] = useState([]);
  const [hoveredMarkerId, setHoveredMarkerId] = useState(null);
  const [selectedLegendItems, setSelectedLegendItems] = useState([]);
  const [map, setMap] = React.useState(null);
  const mapRef = useRef(null);
  const polyInstances = useRef([]);
  const [mapbounds, setMapbounds] = useState(DEFAULT_MAP_BOUNDS);
  const hasUserInteractedRef = useRef(false);
  const isAutoFittingRef = useRef(false);
  const [mapCenter, setMapCenter] = useState(center);
  const userHasZoomedRef = useRef(false);
  const isInitialZoomEventRef = useRef(true);

  useEffect(() => {
    polyInstances.current.forEach(poly => poly?.setMap(null));
    polyInstances.current = [];
  }, [polylines]);
  useEffect(() => {
    setHoveredMarkerId(hoveredMarker);
  }, [hoveredMarker]);

  useEffect(() => {
    if (!enableRecenter && userHasZoomedRef.current) {
      return;
    }
    setMapCenter(center);
  }, [center, enableRecenter]);

  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey,
    libraries,
  });
  const unavailableMessage = isMissingGoogleMapsApiKey
    ? 'Maps is unavailable'
    : googleMapsAuthError
      ? 'Maps is unavailable. Please verify the Google Maps API key restrictions and authorized domains.'
      : loadError
        ? getGoogleMapsUnavailableMessage(loadError)
        : null;

  const onLoad = React.useCallback(function callback(map) {
    mapRef.current = map;
    setMap(map);
  }, []);

  const onUnmount = React.useCallback(function callback() {
    mapRef.current = null;
    setMap(null);
  }, []);

  const isWithinIndia = position => {
    return (
      position.lat >= INDIA_BOUNDS.south &&
      position.lat <= INDIA_BOUNDS.north &&
      position.lng >= INDIA_BOUNDS.west &&
      position.lng <= INDIA_BOUNDS.east
    );
  };
  const getUniqueByName = arr => {
    return [...new Map(arr.map(item => [item.name, item])).values()];
  };

  const getLegendMarker = type => {
    switch (type) {
      case 'rash-driving':
        return LegendRashDrivingSvg;
      case 'driver-drowsiness':
        return LegendDriverDrowsinessSvg;
      case 'driver-distraction':
        return LegendDriverDistractionSvg;
      case 'sos-alert':
        return LegendSOSSvg;
      case 'actual-pickup-drop':
        return LegendActualPickupDropSvg;
      case 'scheduled-pickup-drop':
        return LegendScheduledPickupDropSvg;
      case 'trip-start':
        return LegendTripStartSvg;
      case 'trip-end':
        return LegendTripEndSvg;
      case 'door-open':
        return LegendOpenDoorSvg;
      case 'current-location':
        return LegendCurrentLocationSvg;
      case 'sos-inprogress':
        return LegendSOSInprogressSvg;
      case 'sos-resolved':
        return LegendSOSResolvedSvg;
      case 'sos-new':
        return LegendSOSSvg;
      case 'overspeed':
        return LegendOverspeedSvg;
    }
  };

  const handleUserInteraction = () => {
    if (!preserveViewportOnUserInteraction) return;
    if (isAutoFittingRef.current) return;
    hasUserInteractedRef.current = true;
    onUserInteraction && onUserInteraction();
  };

  const handleZoomChanged = () => {
    if (isInitialZoomEventRef.current) {
      isInitialZoomEventRef.current = false;
      return;
    }
    userHasZoomedRef.current = true;
    handleUserInteraction();
  };

  const handleDragStart = () => {
    userHasZoomedRef.current = true;
    handleUserInteraction();
  };

  useEffect(() => {
    if (mapRef.current && markers.length > 0) {
      const shouldFitBounds =
        !preserveViewportOnUserInteraction || !hasUserInteractedRef.current;
      const nonGPSmarkersWithinIndia = markers.filter(
        marker =>
          isWithinIndia(marker.position) &&
          !marker?.key?.includes('live-location')
      );

      if (nonGPSmarkersWithinIndia.length > 0 && !isGPSLocationUpdated) {
        const markerBounds = new window.google.maps.LatLngBounds();
        nonGPSmarkersWithinIndia.forEach(marker => {
          // to not change zoom level based on live location markers
          markerBounds.extend(marker.position);
        });

        // Ensure the marker bounds don't exceed India's bounds
        const finalBounds = new window.google.maps.LatLngBounds(
          new window.google.maps.LatLng(
            Math.max(markerBounds.getSouthWest().lat(), INDIA_BOUNDS.south),
            Math.max(markerBounds.getSouthWest().lng(), INDIA_BOUNDS.west)
          ),
          new window.google.maps.LatLng(
            Math.min(markerBounds.getNorthEast().lat(), INDIA_BOUNDS.north),
            Math.min(markerBounds.getNorthEast().lng(), INDIA_BOUNDS.east)
          )
        );

        setMapbounds(finalBounds);
        if (enableRecenter || !userHasZoomedRef.current) {
          if (shouldFitBounds) {
            isAutoFittingRef.current = true;
            mapRef.current.fitBounds(finalBounds);
          }
        }
      } else {
        setMapbounds(DEFAULT_MAP_BOUNDS);
      }

      // Ensure the map doesn't zoom too far out
      const mapInstance = mapRef.current;
      const listener = mapInstance.addListener('idle', () => {
        if (mapInstance.getZoom() < 4) {
          mapInstance.setZoom(4);
        }
        isAutoFittingRef.current = false;
        window.google.maps.event.removeListener(listener);
      });
    }
    if (markers?.length > 0) {
      const formattedMarkers = getUniqueByName(markers).map(item => ({
        icon: getLegendMarker(item?.name),
        size: {
          height: '24px',
          width: '24px',
        },
        title: kebabToTitleCase(item?.name),
      }));
      setMapLegendList(formattedMarkers);
      setSelectedLegendItems(formattedMarkers.map(item => item.title));
    } else {
      setMapLegendList([]);
      setSelectedLegendItems([]);
    }
  }, [markers]);

  useEffect(() => {
    if (!map) return;
    if (circles?.length > 0) {
      const newMapCircles = circles?.map(
        circleData =>
          new window.google.maps.Circle({
            map,
            center: circleData.center,
            radius: circleData.radius,
            options: circleData.options,
          })
      );

      setMapCircles(newMapCircles);
    }
    // Clean up function to remove circles when component unmounts
    return () => {
      mapCircles?.forEach(circle => circle.setMap(null));
    };
  }, [map, circles]);

  useEffect(() => {
    setShowInfoWindow(infoWindow?.isVisible);
  }, [infoWindow]);

  const handleMouseOver = (marker, index) => {
    setHoveredMarkerId(marker?.key || index);
    if (marker?.name === 'driver-drowsiness-count') {
      marker.icon.fillOpacity = 0.8;
      marker.icon.strokeWeight = 0;
      marker.icon.scale = 20;
    }
    marker?.onMarkerClick && marker?.onMarkerClick();
  };

  const handleMouseOut = marker => {
    setHoveredMarkerId(null);

    if (marker?.name === 'driver-drowsiness-count') {
      marker.icon.fillOpacity = 1;
      marker.icon.strokeWeight = 2;
      marker.icon.scale = 14;
    }
    isInfoWindowClosable && closeInfoWindow();
  };

  const handleRecenter = () => {
    if (mapRef.current && mapbounds) {
      if (preserveViewportOnUserInteraction) {
        hasUserInteractedRef.current = false;
      }
      isAutoFittingRef.current = true;
      mapRef.current.fitBounds(mapbounds);
    }
  };

  const getCircleMarkerIcon = (marker, isHovered, isOtherHovered) => {
    let iconConfig = {
      ...marker?.icon,
      path: window.google.maps.SymbolPath.CIRCLE,
    };

    if (isOtherHovered && showMarkerHoverEffect) {
      iconConfig = {
        ...iconConfig,
        fillColor: '#CCCCCC',
        strokeColor: '#CCCCCC',
        fillOpacity: 0.6,
        strokeOpacity: 0.6,
      };
    } else if (isHovered && showMarkerHoverEffect) {
      iconConfig = {
        ...iconConfig,
        scale: (marker?.icon?.scale || 20) * 1.2,
      };
    }

    if (isHovered && showMarkerHoverEffectOnHovered) {
      iconConfig = {
        ...iconConfig,
        fillColor:
          marker?.hoveredBackgroundColor || marker?.icon?.backgroundColor,
        strokeColor:
          marker?.hoveredBackgroundColor || marker?.icon?.backgroundColor,
        fillOpacity: 1,
        strokeOpacity: 1,
      };
    }

    return iconConfig;
  };

  const getDefaultMarkerIcon = (marker, isHovered, isOtherHovered) => {
    let baseIcon = {
      ...marker?.icon,
      anchor: new window.google.maps.Point(
        marker?.icon?.anchor?.x,
        marker?.icon?.anchor?.y
      ),
      labelOrigin: new window.google.maps.Point(
        marker?.icon?.labelOrigin?.x || 0,
        marker?.icon?.labelOrigin?.y || 0
      ),
      scaledSize: new window.google.maps.Size(
        marker?.icon?.scaledSize?.x,
        marker?.icon?.scaledSize?.y
      ),
    };

    if (showMarkerHoverEffect && isOtherHovered) {
      const dimmedIcon = createDimmedMarker(marker);
      baseIcon = {
        ...baseIcon,
        url: dimmedIcon.url,
      };
    } else if (showMarkerHoverEffect && isHovered) {
      baseIcon.scaledSize = new window.google.maps.Size(
        marker?.icon?.scaledSize?.x * 1.2,
        marker?.icon?.scaledSize?.y * 1.2
      );
      baseIcon.anchor = new window.google.maps.Point(
        marker?.icon?.anchor?.x * 1.2,
        marker?.icon?.anchor?.y * 1.2
      );
    }

    if (isHovered && showMarkerHoverEffectOnHovered) {
      const hoveredIcon = createDimmedMarker(
        marker,
        marker?.hoveredBackgroundColor || marker?.icon?.backgroundColor
      );

      baseIcon = {
        ...baseIcon,
        url: hoveredIcon?.url || baseIcon.url,
      };
    }

    return baseIcon;
  };

  const getMarkerIcon = (marker, index) => {
    const isHovered = hoveredMarkerId === (marker?.key || index);
    const isOtherHovered =
      hoveredMarkerId && hoveredMarkerId !== (marker?.key || index);

    return marker?.type === 'circle'
      ? getCircleMarkerIcon(marker, isHovered, isOtherHovered)
      : getDefaultMarkerIcon(marker, isHovered, isOtherHovered);
  };

  useEffect(() => {
    if (mapLegendList?.length > 0) {
      setSelectedLegendItems(mapLegendList.map(item => item.title));
    }
  }, [mapLegendList]);

  const filteredMarkers =
    selectedLegendItems.length > 0
      ? markers.filter(marker =>
          selectedLegendItems.includes(kebabToTitleCase(marker?.name))
        )
      : [];

  return unavailableMessage ? (
    <EmptyCard description={<Text>{unavailableMessage}</Text>} />
  ) : isLoaded ? (
    <GoogleMap
      mapContainerStyle={customStyle}
      center={mapCenter}
      zoom={process.env.REACT_APP_ENVIRONMENT === TEST ? 0 : 10}
      onLoad={onLoad}
      onUnmount={onUnmount}
      onDragStart={handleDragStart}
      onZoomChanged={handleZoomChanged}
      map={map}
      options={{
        mapTypeId: 'roadmap',
        styles: baseStyles,
        keyboardShortcuts: false,
        mapTypeControl: false,
        streetViewControl: false,
        gestureHandling: 'greedy',
        rotateControl: false,
        fullscreenControl: !showCustomFullScreenControl,
        restriction: {
          latLngBounds: INDIA_BOUNDS,
          strictBounds: false,
        },
      }}
    >
      {gpsLoadingMessage?.message && (
        <Alert
          className="modal-alert"
          style={{
            top: 10,
            left: '50%',
            transform: 'translateX(-50%)',
            width: 'max-content',
          }}
          message={gpsLoadingMessage?.message}
          banner
          type={gpsLoadingMessage?.type}
          icon={
            gpsLoadingMessage?.type === 'info' ? (
              <LoadingOutlined
                style={{ fontSize: '20px', marginRight: '12px' }}
                spin
              />
            ) : (
              <CheckCircleOutlined
                style={{ fontSize: '20px', marginRight: '12px' }}
              />
            )
          }
        />
      )}
      {showCustomFullScreenControl && CustomFullScreenControl}
      {showInfoWindow && infoWindow?.content && (
        <InfoWindow
          position={infoWindow?.position}
          onCloseClick={closeInfoWindow}
        >
          <ZoomBlocker>
            <InfoWindowContent
              infoWindow={infoWindow}
              onClose={closeInfoWindow}
              className={
                (infoWindow?.key?.includes('checkpoint') ||
                  infoWindow?.key?.includes('drowsiness')) &&
                'small'
              }
            />
          </ZoomBlocker>
        </InfoWindow>
      )}
      {filteredMarkers?.map((marker, index) => {
        if (marker?.type === 'circle') {
          return (
            <React.Fragment key={marker?.key || index}>
              <Marker
                position={marker?.position}
                icon={
                  showMarkerHoverEffect
                    ? getMarkerIcon(marker, index)
                    : {
                        ...marker?.icon,
                        path: window.google.maps.SymbolPath.CIRCLE,
                      }
                }
                label={marker?.label}
                onMouseOver={() => handleMouseOver(marker, index)}
                onMouseOut={() => handleMouseOut(marker)}
                map={map}
                collisionBehavior="OPTIONAL_AND_HIDES_HIGHEST"
                zIndex={
                  hoveredMarkerId === (marker?.key || index) ? 9999 : index + 1
                }
              />
              {marker?.label?.className && (
                <style>{`
            .${marker?.label?.className}::after {
              content: url(${marker.label?.icon});
              display: inline-block;
              position: absolute;
              transform: translate(-50%, -50%);
              transition: transform 0.3s ease;
            }
          `}</style>
              )}
            </React.Fragment>
          );
        } else if (marker?.name === 'current-location-with-heading') {
          return (
            <RotatedMarker
              key={marker?.key || index}
              position={marker?.position}
              iconUrl={marker?.icon?.url || marker?.icon}
              rotation={marker?.heading || 0}
              size={marker?.icon?.scaledSize?.x || 40}
              onClick={marker?.onMarkerClick}
            />
          );
        } else {
          return (
            <Marker
              key={marker?.key || index}
              position={marker?.position}
              icon={
                showMarkerHoverEffect || showMarkerHoverEffectOnHovered
                  ? getMarkerIcon(marker, index)
                  : {
                      ...marker?.icon,
                      anchor: new window.google.maps.Point(
                        marker?.icon?.anchor?.x,
                        marker?.icon?.anchor?.y
                      ),
                      labelOrigin: new window.google.maps.Point(
                        marker?.icon?.labelOrigin?.x || 0,
                        marker?.icon?.labelOrigin?.y || 0
                      ),
                      scaledSize: new window.google.maps.Size(
                        marker?.icon?.scaledSize?.x,
                        marker?.icon?.scaledSize?.y
                      ),
                    }
              }
              label={marker?.label}
              onMouseOver={() => handleMouseOver(marker, index)}
              onMouseOut={() => handleMouseOut(marker)}
              map={map}
              zIndex={
                hoveredMarkerId === (marker?.key || index) ? 9999 : index + 1
              }
            />
          );
        }
      })}
      {polylines?.map(pathData => (
        <React.Fragment key={pathData?.key}>
          {/* border for path */}
          <Polyline
            key={pathData?.key + '-border'}
            path={pathData?.path}
            options={{
              strokeColor: pathData?.borderColor,
              geodesic: true,
              strokeOpacity: 1,
              strokeWeight: (pathData?.normalWidth || 2) + 2,
              map: map,
              zIndex: 1,
            }}
            onLoad={poly => {
              polyInstances.current.push(poly);
            }}
          />
          {/* main path */}
          <Polyline
            key={pathData?.key + '-main'}
            path={pathData?.path}
            options={{
              strokeColor: pathData?.strokeColor,
              geodesic: true,
              strokeOpacity: 1,
              strokeWeight: pathData?.normalWidth,
              map: map,
              zIndex: 2,
            }}
            onLoad={poly => {
              polyInstances.current.push(poly);
            }}
          />
        </React.Fragment>
      ))}
      {showFilter && <MapFilter {...mapFilterProps} />}
      {showLegend && mapLegendList?.length > 0 && (
        <MapLegend
          mapLegendList={mapLegendList}
          isVertical={isMapLegendVertical}
          showFilter={showFilter}
          onSelectionChange={setSelectedLegendItems}
        />
      )}
      <ConfigProvider
        theme={{
          components: {
            Button: {
              defaultBg: '#ffffff',
              defaultHoverBg: '#ffffff',
              defaultBorderColor: '#ffffff',
              defaultHoverBorderColor: '#ffffff',
            },
            Tooltip: {
              colorTextLightSolid: 'var(--primary-text-color, #000000)',
            },
          },
        }}
      >
        <Flex
          style={{
            position: 'absolute',
            top: 10,
            right: 60,
          }}
        >
          <Tooltip title="Recenter Map">
            <Button
              size="small"
              onClick={handleRecenter}
              icon={<Icon component={Recenter} />}
              className="recenter-button"
            />
          </Tooltip>
        </Flex>
      </ConfigProvider>
    </GoogleMap>
  ) : (
    <>Loading...</>
  );
};

GoogleMaps.propTypes = {
  polylines: PropTypes.arrayOf(PropTypes.object),
  infoWindow: PropTypes.object,
  closeInfoWindow: PropTypes.func,
  center: PropTypes.shape({
    lat: PropTypes.number,
    lng: PropTypes.number,
  }),
  showLegend: PropTypes.bool,
  mapFilterProps: PropTypes.object,
  showFilter: PropTypes.bool,
  isMapLegendVertical: PropTypes.bool,
  markers: PropTypes.arrayOf(PropTypes.object),
  circles: PropTypes.arrayOf(PropTypes.object),
  customStyle: PropTypes.object,
  showCustomFullScreenControl: PropTypes.bool,
  CustomFullScreenControl: PropTypes.node,
  isGPSLocationUpdated: PropTypes.bool,
  gpsLoadingMessage: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  InfoWindowContent: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.elementType,
  ]),
  isInfoWindowClosable: PropTypes.bool,
  showMarkerHoverEffect: PropTypes.bool,
  hoveredMarker: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  enableRecenter: PropTypes.bool,
  preserveViewportOnUserInteraction: PropTypes.bool,
  onUserInteraction: PropTypes.func,
};

export default React.memo(GoogleMaps);
